"use client";

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ApiClientError } from "@/lib/api-client";
import { useLocale } from "@/lib/locale/locale-provider";
import { useTheme } from "@/lib/theme/them-provider";
import { fetchGiftOptions, submitGiftSelection, type GiftOption } from "@/src/features/wedding/api/gifts";
import { Container } from "../ui/container";
import { Stack } from "../ui/stack";

import type { GiftItem, GiftsProps } from "@/lib/sections/gifts.types";
import { FadeInSection } from "@/components/animations/FadeIn";
import { GiftCard } from "@/components/gifts/GiftCard";
import { SectionBackdrop } from "@/components/sections/SectionBackdrop";
import { GiftSelectionModal } from "../gifts/GiftSelectionModal";

const PRESET_PAYPAL_AMOUNTS = [20, 100, 200, 500];
type BankDetail = NonNullable<GiftsProps["bankDetails"]>[number];

function mapRemoteParticipants(option: GiftOption): GiftItem["participants"] {
  return option.participants.map((participant) => ({
    name: participant.name,
    amount: typeof participant.amount === "number" ? participant.amount : undefined,
    note: participant.note ?? undefined,
    createdAt: participant.createdAt,
  }));
}

function safeCurrency(gift: GiftItem) {
  return gift.currency ?? "EUR";
}

function computeStatus(gift: GiftItem) {
  const slotsTarget = gift.targetSlots;
  const amountTarget = gift.targetAmount;

  const slotsCount = gift.contributionCount ?? gift.participants.length;
  const amountSum =
    typeof gift.totalAmount === "number"
      ? gift.totalAmount
      : gift.participants.reduce((sum, participant) => sum + (participant.amount ?? 0), 0);

  if (typeof slotsTarget === "number") {
    if (slotsCount <= 0) return "free";
    if (slotsCount < slotsTarget) return "partial";
    return "reserved";
  }

  if (typeof amountTarget === "number") {
    if (amountSum <= 0) return "free";
    if (amountSum < amountTarget) return "partial";
    return "reserved";
  }

  if (slotsCount <= 0 && amountSum <= 0) {
    return "free";
  }

  return "partial";
}

function findBankEntry(details: BankDetail[] | undefined, patterns: string[]) {
  if (!details) return undefined;
  return details.find((item) => patterns.some((pattern) => item.label.toLowerCase().includes(pattern)));
}

function findExactBankEntry(details: BankDetail[] | undefined, labels: string[]) {
  if (!details) return undefined;
  const normalizedLabels = labels.map((label) => label.toLowerCase().trim());
  return details.find((item) => normalizedLabels.includes(item.label.toLowerCase().trim()));
}

function formatIban(value: string | undefined) {
  if (!value) return "---- ---- ---- ----";
  const compact = value.replace(/\s+/g, "");
  return compact.replace(/(.{4})/g, "$1 ").trim();
}

function hexToRgba(hex: string, alpha: number) {
  const clean = hex.replace("#", "");
  const normalized =
    clean.length === 3
      ? clean
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : clean;

  if (normalized.length !== 6) {
    return `rgba(183, 110, 121, ${alpha})`;
  }

  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getPaymentMethodTone(label: string, fallbackPrimary: string) {
  const normalized = label.trim().toLowerCase();

  if (normalized.includes("orange")) {
    return {
      accent: "#f58220",
      background: "linear-gradient(180deg, rgba(245,130,32,0.08) 0%, rgba(255,255,255,0.96) 100%)",
      border: "rgba(245,130,32,0.22)",
      badge: "ORANGE",
    };
  }

  if (normalized.includes("mtn")) {
    return {
      accent: "#c89d10",
      background: "linear-gradient(180deg, rgba(232,192,32,0.1) 0%, rgba(255,255,255,0.96) 100%)",
      border: "rgba(200,157,16,0.22)",
      badge: "MTN",
    };
  }

  return {
    accent: fallbackPrimary,
    background: `linear-gradient(180deg, ${hexToRgba(fallbackPrimary, 0.06)} 0%, rgba(255,255,255,0.96) 100%)`,
    border: hexToRgba(fallbackPrimary, 0.16),
    badge: "MOBILE",
  };
}

function toExternalHref(value: string | undefined) {
  if (!value) return undefined;
  if (value.startsWith("https://") || value.startsWith("http://")) return value;
  return undefined;
}

function toPhoneHref(value: string | undefined) {
  if (!value) return undefined;
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return undefined;
  return trimmed.startsWith("+") ? `tel:+${digits}` : `tel:${digits}`;
}

function toDetailHref(value: string | undefined) {
  return toExternalHref(value) ?? toPhoneHref(value);
}

function hasCopyablePaymentValue(value: string) {
  return value.trim().length > 0;
}

function hasActionablePaymentValue(value: string) {
  return Boolean(toDetailHref(value));
}

function parsePayPal(value: string | undefined) {
  if (!value) {
    return { display: "", baseHref: undefined as string | undefined, isPool: false };
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return { display: "", baseHref: undefined as string | undefined, isPool: false };
  }

  if (trimmed.startsWith("https://") || trimmed.startsWith("http://")) {
    try {
      const url = new URL(trimmed);
      const host = url.hostname.toLowerCase();
      const isPayPalMeHost = host.includes("paypal.me");
      const isPayPalMePath =
        host.includes("paypal.com") && url.pathname.toLowerCase().startsWith("/paypalme/");
      const isPool = host.includes("paypal.com") && url.pathname.toLowerCase().startsWith("/pool/");

      if (isPayPalMeHost || isPayPalMePath) {
        const pathParts = url.pathname.split("/").filter(Boolean);
        const username = isPayPalMePath ? pathParts[1] : pathParts[0];
        if (username) {
          return {
            display: trimmed,
            baseHref: `https://www.paypal.com/paypalme/${encodeURIComponent(username)}`,
            isPool: false,
          };
        }
      }

      if (isPool) {
        return { display: trimmed, baseHref: trimmed, isPool: true };
      }
    } catch {
      return { display: trimmed, baseHref: undefined as string | undefined, isPool: false };
    }

    return { display: trimmed, baseHref: toExternalHref(trimmed), isPool: false };
  }

  const normalized = trimmed
    .replace(/^paypal\.me\//i, "")
    .replace(/^www\.paypal\.me\//i, "")
    .replace(/^@/, "");

  if (!normalized.includes(" ")) {
    return {
      display: trimmed,
      baseHref: `https://www.paypal.com/paypalme/${encodeURIComponent(normalized)}`,
      isPool: false,
    };
  }

  return { display: trimmed, baseHref: undefined as string | undefined, isPool: false };
}

function buildPayPalSendHref(params: {
  baseHref: string | undefined;
  amount: string;
  currency: string;
  note: string;
  isPool: boolean;
}) {
  const { baseHref, amount, currency, note, isPool } = params;
  if (!baseHref) return undefined;
  if (isPool) return baseHref;

  const normalizedAmount = Number(amount.replace(",", "."));
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
    return undefined;
  }

  try {
    const url = new URL(baseHref);
    const isPayPalMe =
      url.hostname.includes("paypal.com") && url.pathname.toLowerCase().startsWith("/paypalme/");

    if (isPayPalMe) {
      url.pathname = `${url.pathname.replace(/\/+$/, "")}/${normalizedAmount.toFixed(2)}`;
      url.searchParams.set("currencyCode", currency);
      if (note.trim()) {
        url.searchParams.set("note", note.trim());
      }
      return url.toString();
    }

    url.searchParams.set("amount", normalizedAmount.toFixed(2));
    url.searchParams.set("currency_code", currency);
    if (note.trim()) {
      url.searchParams.set("note", note.trim());
    }
    return url.toString();
  } catch {
    return baseHref;
  }
}

function mapRemoteGiftOption(option: GiftOption): GiftItem {
  return {
    id: option.id,
    title: option.title,
    description: option.description ?? undefined,
    image: option.imageUrl ?? "/gifts/flight.jpg",
    suggestedAmount: typeof option.suggestedAmount === "number" ? option.suggestedAmount : undefined,
    contributionCount: option.contributionCount,
    totalAmount: option.totalAmount,
    currency: option.currency,
    kind: option.kind,
    participants: mapRemoteParticipants(option),
  };
}

function mergeGiftItems(localItems: GiftItem[], remoteItems: GiftOption[]) {
  if (!remoteItems.length) {
    return localItems;
  }

  const remoteMap = new Map(remoteItems.map((item) => [item.id, item]));
  const localIds = new Set(localItems.map((item) => item.id));

  const mergedLocal = localItems.map((item) => {
    const remote = remoteMap.get(item.id);
    if (!remote) {
      return item;
    }

    return {
      ...item,
      title: item.title || remote.title,
      description: item.description ?? remote.description ?? undefined,
      image: item.image || remote.imageUrl || "/gifts/flight.jpg",
      currency: remote.currency || item.currency,
      suggestedAmount:
        typeof remote.suggestedAmount === "number" ? remote.suggestedAmount : item.suggestedAmount,
      contributionCount: remote.contributionCount,
      totalAmount: remote.totalAmount,
      kind: remote.kind,
      participants: remote.participants.length ? mapRemoteParticipants(remote) : item.participants,
    };
  });

  const appendedRemote = remoteItems
    .filter((item) => !localIds.has(item.id))
    .map(mapRemoteGiftOption);

  return [...mergedLocal, ...appendedRemote];
}

export function GiftsSection({
  title,
  text,
  bankDetails,
  gifts = [],
  onGiftsChange,
}: GiftsProps) {
  const { theme } = useTheme();
  const locale = useLocale();
  const [items, setItems] = useState<GiftItem[]>(gifts);
  const [activeGiftId, setActiveGiftId] = useState<string | null>(null);
  const [selectedPresetPaypalAmount, setSelectedPresetPaypalAmount] = useState<number | null>(null);
  const [paypalAmount, setPaypalAmount] = useState("");
  const [copiedDetailKey, setCopiedDetailKey] = useState<string | null>(null);
  const [giftLoadError, setGiftLoadError] = useState<string | null>(null);
  const [isLoadingRemoteGifts, setIsLoadingRemoteGifts] = useState(false);

  const copy = useMemo(() => {
    if (locale === "fr") {
      return {
        title: "Cadeaux",
        fallbackLoadError:
          "Les cadeaux s'affichent en mode local pour le moment. Vous pouvez quand même envoyer une contribution.",
        loading: "Chargement des options de cadeaux...",
        alternativeSupport: "Si vous préférez nous soutenir autrement :",
        accountHolder: "TITULAIRE",
        bic: "BIC",
        reference: "RÉFÉRENCE",
        copyButton: "Copier",
        copiedButton: "Copie",
        copyAccountAction: "Copier le titulaire",
        copyIbanAction: "Copier l'IBAN",
        copyBicAction: "Copier le BIC",
        copyReferenceAction: "Copier la reference",
        openLinkAction: "Ouvrir",
        callNumberAction: "Appeler",
        paypalTitle: "Envoyer un cadeau via PayPal",
        presetAmount: "Montant rapide",
        customAmount: "Autre montant",
        paypalPlaceholder: "par ex. 50,00",
        paypalAction: (amountLabel: string | null) =>
          amountLabel ? `Envoyer ${amountLabel} avec PayPal` : "Envoyer avec PayPal",
        paypalSecurity: "Redirection sécurisée vers PayPal.",
        paypalPoolHint: "Choisissez un montant ici, puis confirmez-le dans notre cagnotte PayPal.",
        paypalPoolAction: (amountLabel: string | null) =>
          amountLabel
            ? `Continuer avec ${amountLabel} vers la cagnotte PayPal`
            : "Ouvrir la cagnotte PayPal",
        paypalNote: "Mariage",
        paypalPoolSecurity: "Redirection sÃ©curisÃ©e vers PayPal. Le montant final sera confirmÃ© sur PayPal.",
        otherPaymentsTitle: "Paiement mobile",
        otherPaymentsSingleHint: "Utilisez directement le numero ci-dessous pour votre participation.",
        otherPaymentsPendingHint: "Le numero Orange Money peut etre ajoute ici des qu'il est pret.",
        otherPaymentsHint:
          "Vous pouvez aussi envoyer votre participation via Orange Money ou MTN Mobile Money.",
      };
    }

    if (locale === "en") {
      return {
        title: "Gifts",
        fallbackLoadError:
          "Gift options are currently shown from the local fallback. You can still send a contribution.",
        loading: "Loading gift options...",
        alternativeSupport: "If you would rather support us in another way:",
        accountHolder: "ACCOUNT HOLDER",
        bic: "BIC",
        reference: "REFERENCE",
        copyButton: "Copy",
        copiedButton: "Copied",
        copyAccountAction: "Copy account holder",
        copyIbanAction: "Copy IBAN",
        copyBicAction: "Copy BIC",
        copyReferenceAction: "Copy reference",
        openLinkAction: "Open",
        callNumberAction: "Call",
        paypalTitle: "Send a gift with PayPal",
        presetAmount: "Quick amount",
        customAmount: "Custom amount",
        paypalPlaceholder: "e.g. 50.00",
        paypalAction: (amountLabel: string | null) =>
          amountLabel ? `Send ${amountLabel} with PayPal` : "Send with PayPal",
        paypalSecurity: "Secure redirect to PayPal.",
        paypalPoolHint: "Choose an amount here, then confirm it in our PayPal pool.",
        paypalPoolAction: (amountLabel: string | null) =>
          amountLabel ? `Continue with ${amountLabel} to PayPal Pool` : "Open PayPal Pool",
        paypalNote: "Wedding",
        paypalPoolSecurity: "Secure redirect to PayPal. The final amount is confirmed on PayPal.",
        otherPaymentsTitle: "Mobile payment",
        otherPaymentsSingleHint: "Use the number below directly for your contribution.",
        otherPaymentsPendingHint: "The Orange Money number can be added here as soon as it is ready.",
        otherPaymentsHint:
          "You can also send your contribution via Orange Money or MTN Mobile Money.",
      };
    }

    return {
      title: "Geschenke",
      fallbackLoadError:
        "Die Geschenke werden gerade aus der lokalen Fallback-Liste angezeigt. Einen Beitrag koennt ihr trotzdem senden.",
      loading: "Geschenkoptionen werden geladen...",
      alternativeSupport: "Falls ihr uns alternativ unterstuetzen moechtet:",
      accountHolder: "KONTOINHABER",
      bic: "BIC",
      reference: "VERWENDUNGSZWECK",
      copyButton: "Kopieren",
      copiedButton: "Kopiert",
      copyAccountAction: "Kontoinhaber kopieren",
      copyIbanAction: "IBAN kopieren",
      copyBicAction: "BIC kopieren",
      copyReferenceAction: "Verwendungszweck kopieren",
      openLinkAction: "Oeffnen",
      callNumberAction: "Anrufen",
      paypalTitle: "Geschenk per PayPal senden",
      presetAmount: "Schnellbetrag",
      customAmount: "Anderer Betrag",
      paypalPlaceholder: "z.B. 50,00",
      paypalAction: (amountLabel: string | null) =>
        amountLabel ? `${amountLabel} mit PayPal senden` : "Mit PayPal senden",
      paypalSecurity: "Sichere Weiterleitung zu PayPal.",
      paypalPoolHint: "Waehlt hier einen Betrag und bestaetigt ihn dann im PayPal-Pool.",
      paypalPoolAction: (amountLabel: string | null) =>
        amountLabel ? `Mit ${amountLabel} zum PayPal-Pool` : "PayPal-Pool oeffnen",
      paypalNote: "Hochzeit",
      paypalPoolSecurity: "Sichere Weiterleitung zu PayPal. Der endgueltige Betrag wird bei PayPal bestaetigt.",
      otherPaymentsTitle: "Mobile Zahlung",
      otherPaymentsSingleHint: "Nutzt die untenstehende Nummer direkt fuer euren Beitrag.",
      otherPaymentsPendingHint: "Die Orange-Money-Nummer kann hier eingefuegt werden, sobald sie bereit ist.",
      otherPaymentsHint:
        "Ihr koennt euren Beitrag auch per Orange Money oder MTN Mobile Money senden.",
    };
  }, [locale]);

  useEffect(() => {
    setItems(gifts);
  }, [gifts]);

  useEffect(() => {
    if (!copiedDetailKey || typeof window === "undefined") {
      return;
    }

    const timeout = window.setTimeout(() => {
      setCopiedDetailKey(null);
    }, 1800);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [copiedDetailKey]);

  useEffect(() => {
    let cancelled = false;

    async function loadGiftOptions() {
      setIsLoadingRemoteGifts(true);

      try {
        const response = await fetchGiftOptions({
          limit: Math.max(gifts.length, 6) || 6,
        });

        if (cancelled) {
          return;
        }

        setItems((currentItems) => mergeGiftItems(currentItems.length ? currentItems : gifts, response.items));
        setGiftLoadError(null);
      } catch {
        if (!cancelled) {
          setGiftLoadError(copy.fallbackLoadError);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRemoteGifts(false);
        }
      }
    }

    void loadGiftOptions();

    return () => {
      cancelled = true;
    };
  }, [copy.fallbackLoadError, gifts]);

  const activeGift = useMemo(
    () => items.find((gift) => gift.id === activeGiftId) ?? null,
    [items, activeGiftId],
  );

  const accountOwnerEntry = findBankEntry(bankDetails, ["name", "kontoinhaber", "holder", "titulaire"]);
  const ibanEntry = findBankEntry(bankDetails, ["iban"]);
  const bicEntry =
    findExactBankEntry(bankDetails, ["bic"]) ?? findBankEntry(bankDetails, ["swift", "bic"]);
  const referenceEntry = findBankEntry(bankDetails, ["verwendungszweck", "reference", "zweck", "objet"]);
  const paypalEntry = findBankEntry(bankDetails, ["paypal"]);
  const accountOwner = accountOwnerEntry?.value ?? bankDetails?.[0]?.value ?? "";
  const iban = ibanEntry?.value ?? "";
  const bic = bicEntry?.value ?? "";
  const reference = referenceEntry?.value;
  const paypalValue = paypalEntry?.value;
  const paypal = useMemo(() => parsePayPal(paypalValue), [paypalValue]);
  const paypalCurrency = items[0]?.currency ?? "EUR";
  const paypalNote = reference ?? copy.paypalNote;
  const additionalPaymentDetails = useMemo(
    () =>
      bankDetails?.filter(
        (detail) =>
          detail !== accountOwnerEntry &&
          detail !== ibanEntry &&
          detail !== bicEntry &&
          detail !== referenceEntry &&
          detail !== paypalEntry,
      ) ?? [],
    [bankDetails, accountOwnerEntry, ibanEntry, bicEntry, referenceEntry, paypalEntry],
  );
  const resolvedPaypalAmount =
    selectedPresetPaypalAmount !== null ? String(selectedPresetPaypalAmount) : paypalAmount;
  const paymentCardTitle =
    additionalPaymentDetails.length === 1 ? additionalPaymentDetails[0].label : copy.otherPaymentsTitle;
  const paymentCardHasActionableValue = additionalPaymentDetails.some((detail) =>
    hasActionablePaymentValue(detail.value),
  );
  const paymentCardHint =
    additionalPaymentDetails.length === 1
      ? paymentCardHasActionableValue
        ? copy.otherPaymentsSingleHint
        : copy.otherPaymentsPendingHint
      : copy.otherPaymentsHint;
  const paymentCardTone = useMemo(
    () => getPaymentMethodTone(paymentCardTitle, theme.colors.primary),
    [paymentCardTitle, theme.colors.primary],
  );
  const paypalSendHref = useMemo(
    () =>
      buildPayPalSendHref({
        baseHref: paypal.baseHref,
        amount: resolvedPaypalAmount,
        currency: paypalCurrency,
        isPool: paypal.isPool,
        note: paypalNote,
      }),
    [paypal.baseHref, paypal.isPool, resolvedPaypalAmount, paypalCurrency, paypalNote],
  );
  const parsedPaypalAmount = Number(resolvedPaypalAmount.replace(",", "."));
  const hasValidPaypalAmount = Number.isFinite(parsedPaypalAmount) && parsedPaypalAmount > 0;
  const paypalAmountLabel = hasValidPaypalAmount
    ? `${parsedPaypalAmount.toFixed(2).replace(".", ",")} ${paypalCurrency}`
    : null;

  function updateGifts(next: GiftItem[]) {
    setItems(next);
    onGiftsChange?.(next);
  }

  async function copyValue(value: string, key: string) {
    if (typeof navigator === "undefined" || !navigator.clipboard || !value.trim()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value.trim());
      setCopiedDetailKey(key);
    } catch {
      // Ignore clipboard errors and keep the existing UI unchanged.
    }
  }

  function closeGiftModal() {
    const previousGiftId = activeGiftId;
    setActiveGiftId(null);

    if (!previousGiftId || typeof document === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      const trigger = document.querySelector<HTMLButtonElement>(
        `[data-gift-card-id="${previousGiftId}"]`,
      );
      trigger?.focus();
    });
  }

  async function handleAddParticipant(
    giftId: string,
    participant: { name: string; amount?: number; note?: string },
  ) {
    const currentGift = items.find((gift) => gift.id === giftId);
    const response = await submitGiftSelection({
      giftOptionId: giftId,
      name: participant.name.trim(),
      amount: participant.amount,
      message: participant.note?.trim() || undefined,
    });

    const timestamp = new Date().toISOString();

    updateGifts(
      items.map((gift) =>
        gift.id === giftId
          ? {
              ...gift,
              contributionCount: (gift.contributionCount ?? 0) + 1,
              totalAmount: (gift.totalAmount ?? 0) + (participant.amount ?? 0),
              participants: [
                ...gift.participants,
                {
                  ...participant,
                  name: participant.name.trim(),
                  note: participant.note?.trim() || undefined,
                  createdAt: timestamp,
                },
              ],
            }
          : gift,
      ),
    );

    const enteredAmount =
      typeof participant.amount === "number" && participant.amount > 0
        ? participant.amount.toFixed(2)
        : "";
    const redirectHref =
      paypal.baseHref && enteredAmount
        ? buildPayPalSendHref({
            baseHref: paypal.baseHref,
            amount: enteredAmount,
            currency: currentGift?.currency ?? paypalCurrency,
            isPool: paypal.isPool,
            note: currentGift?.title ?? paypalNote,
          })
        : paypal.baseHref;

    return {
      message: response.message,
      redirectHref,
    };
  }

  return (
    <SectionBackdrop tone="gifts">
      <Container>
        <Stack gap="xl">
          <FadeInSection style={{ textAlign: "center", maxWidth: 560, margin: "0 auto" }}>
            <h2
              style={{
                fontFamily: theme.fonts.body,
                color: theme.colors.primary,
                fontSize: "clamp(2rem, 5vw, 2.7rem)",
                fontWeight: 700,
                letterSpacing: "0.01em",
                marginBottom: 16,
              }}
            >
              {title ?? copy.title}
            </h2>

            <Stack gap="md">
              {text.map((paragraph, index) => (
                <p
                  key={`${paragraph}-${index}`}
                  style={{
                    color: theme.colors.textMuted,
                    lineHeight: 1.9,
                    margin: 0,
                  }}
                >
                  {paragraph}
                </p>
              ))}
            </Stack>

            {isLoadingRemoteGifts ? (
              <p style={{ color: theme.colors.textMuted, marginTop: 16, fontSize: 14 }}>
                {copy.loading}
              </p>
            ) : null}

            {giftLoadError ? (
              <p
                role="status"
                style={{
                  marginTop: 16,
                  borderRadius: 18,
                  border: "1px solid rgba(214, 170, 76, 0.25)",
                  background: "rgba(255,255,255,0.72)",
                  padding: "12px 16px",
                  color: theme.colors.text,
                  lineHeight: 1.6,
                }}
              >
                {giftLoadError}
              </p>
            ) : null}
          </FadeInSection>

          {items.length > 0 && (
            <FadeInSection
              style={{
                maxWidth: "100%",
                width: "100%",
              }}
            >
              <div className="grid grid-cols-2 gap-3 md:gap-5 xl:gap-7">
                {items.map((gift, index) => (
                  <GiftCard
                    key={gift.id}
                    gift={gift}
                    status={computeStatus(gift)}
                    currency={safeCurrency(gift)}
                    index={index}
                    onSelect={() => setActiveGiftId(gift.id)}
                  />
                ))}
              </div>
            </FadeInSection>
          )}

          {bankDetails ? (
            <FadeInSection delay={0.08} style={{ maxWidth: 1120, margin: "0 auto", width: "100%" }}>
              <Stack gap="sm">
                <p style={{ color: theme.colors.textMuted, margin: 0 }}>{copy.alternativeSupport}</p>

                <div
                  className={`grid grid-cols-1 gap-5 ${
                    additionalPaymentDetails.length > 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"
                  } lg:items-stretch`}
                >
                  <div
                    style={{
                      position: "relative",
                      borderRadius: 28,
                      overflow: "hidden",
                      padding: "24px 24px 22px",
                      color: "#ffffff",
                      background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${hexToRgba(
                        theme.colors.primary,
                        0.9,
                      )} 58%, ${hexToRgba(theme.colors.primary, 0.78)} 100%)`,
                      minHeight: 220,
                      height: "100%",
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.1)",
                        top: -150,
                        right: -40,
                      }}
                    />
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.08)",
                        bottom: -120,
                        left: -60,
                      }}
                    />

                    <div style={{ position: "relative", zIndex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 16,
                        }}
                      >
                        <p style={{ margin: 0, fontWeight: 800, letterSpacing: 0.6, fontSize: 30 }}>BANK</p>
                        <p style={{ margin: 0, fontSize: 20, opacity: 0.9 }}>Online</p>
                      </div>

                      <p
                        style={{
                          margin: "38px 0 18px",
                          fontSize: 32,
                          letterSpacing: 1.1,
                          fontWeight: 500,
                          lineHeight: 1.2,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {formatIban(iban)}
                      </p>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 16,
                          alignItems: "flex-end",
                        }}
                      >
                        <div>
                          <p style={{ margin: 0, opacity: 0.85, fontSize: 12, letterSpacing: 1.5 }}>
                            {copy.accountHolder}
                          </p>
                          <p style={{ margin: "4px 0 0", fontSize: 24 }}>{accountOwner || "N/A"}</p>
                        </div>
                        {bic ? (
                          <div style={{ textAlign: "right" }}>
                            <p style={{ margin: 0, opacity: 0.85, fontSize: 12, letterSpacing: 1.2 }}>
                              {copy.bic}
                            </p>
                            <p style={{ margin: "4px 0 0", fontSize: 18 }}>{bic}</p>
                          </div>
                        ) : reference ? (
                          <div style={{ textAlign: "right" }}>
                            <p style={{ margin: 0, opacity: 0.85, fontSize: 12, letterSpacing: 1.2 }}>
                              {copy.reference}
                            </p>
                            <p style={{ margin: "4px 0 0", fontSize: 18 }}>{reference}</p>
                          </div>
                        ) : null}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          marginTop: 22,
                        }}
                      >
                        {accountOwner ? (
                          <button
                            type="button"
                            onClick={() => void copyValue(accountOwner, "bank-account-owner")}
                            style={{
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.34)",
                              background:
                                copiedDetailKey === "bank-account-owner" ? "rgba(255,255,255,0.22)" : "transparent",
                              color: "#ffffff",
                              padding: "9px 14px",
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {copiedDetailKey === "bank-account-owner"
                              ? copy.copiedButton
                              : copy.copyAccountAction}
                          </button>
                        ) : null}
                        {iban ? (
                          <button
                            type="button"
                            onClick={() => void copyValue(iban, "bank-iban")}
                            style={{
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.34)",
                              background: copiedDetailKey === "bank-iban" ? "rgba(255,255,255,0.22)" : "transparent",
                              color: "#ffffff",
                              padding: "9px 14px",
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {copiedDetailKey === "bank-iban" ? copy.copiedButton : copy.copyIbanAction}
                          </button>
                        ) : null}
                        {bic ? (
                          <button
                            type="button"
                            onClick={() => void copyValue(bic, "bank-bic")}
                            style={{
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.34)",
                              background: copiedDetailKey === "bank-bic" ? "rgba(255,255,255,0.22)" : "transparent",
                              color: "#ffffff",
                              padding: "9px 14px",
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {copiedDetailKey === "bank-bic" ? copy.copiedButton : copy.copyBicAction}
                          </button>
                        ) : null}
                        {reference ? (
                          <button
                            type="button"
                            onClick={() => void copyValue(reference, "bank-reference")}
                            style={{
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.34)",
                              background:
                                copiedDetailKey === "bank-reference" ? "rgba(255,255,255,0.22)" : "transparent",
                              color: "#ffffff",
                              padding: "9px 14px",
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {copiedDetailKey === "bank-reference"
                              ? copy.copiedButton
                              : copy.copyReferenceAction}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {paypalValue ? (
                    <div
                      style={{
                        borderRadius: 28,
                        border: `1px solid ${theme.colors.border}`,
                        background: theme.colors.surface,
                        padding: "24px 24px 26px",
                        height: "100%",
                      }}
                    >
                      <Stack gap="md">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 16,
                          }}
                        >
                          <h3
                            style={{
                              margin: 0,
                              fontFamily: theme.fonts.heading,
                              color: theme.colors.primary,
                              fontSize: "clamp(2rem, 7vw, 3rem)",
                              lineHeight: 1.15,
                            }}
                          >
                            {copy.paypalTitle}
                          </h3>
                          <span
                            aria-hidden="true"
                            style={{
                              color: theme.colors.primary,
                              fontSize: 30,
                              fontWeight: 800,
                              lineHeight: 1,
                              alignSelf: "center",
                            }}
                          >
                            P
                          </span>
                        </div>

                        {paypal.isPool ? (
                          <p style={{ margin: 0, color: theme.colors.text, lineHeight: 1.6 }}>
                            {copy.paypalPoolHint}
                          </p>
                        ) : null}

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "nowrap",
                            justifyContent: "flex-start",
                            width: "100%",
                            overflowX: "auto",
                            paddingBottom: 2,
                          }}
                        >
                          {PRESET_PAYPAL_AMOUNTS.map((amount) => {
                            const isActive = selectedPresetPaypalAmount === amount;
                            return (
                              <button
                                key={amount}
                                type="button"
                                onClick={() => {
                                  setSelectedPresetPaypalAmount(amount);
                                  setPaypalAmount("");
                                }}
                                style={{
                                  borderRadius: 999,
                                  border: `1px solid ${
                                    isActive ? theme.colors.primary : hexToRgba(theme.colors.primary, 0.45)
                                  }`,
                                  padding: "10px 0",
                                  minWidth: 96,
                                  background: isActive ? hexToRgba(theme.colors.primary, 0.14) : "#ffffff",
                                  color: theme.colors.primary,
                                  fontFamily: theme.fonts.heading,
                                  fontSize: 18,
                                  lineHeight: 1,
                                  cursor: "pointer",
                                }}
                              >
                                {amount} {paypalCurrency}
                              </button>
                            );
                          })}
                        </div>

                        <div style={{ display: "grid", gap: 10 }}>
                          <label
                            htmlFor="paypal-amount"
                            style={{ color: theme.colors.text, fontSize: 16, fontWeight: 600 }}
                          >
                            {copy.customAmount}
                          </label>
                          <input
                            id="paypal-amount"
                            name="paypal-amount"
                            inputMode="decimal"
                            value={paypalAmount}
                            onChange={(event) => {
                              setPaypalAmount(event.target.value);
                              setSelectedPresetPaypalAmount(null);
                            }}
                            placeholder={copy.paypalPlaceholder}
                            style={{
                              width: "100%",
                              border: "none",
                              borderBottom: `1px solid ${theme.colors.border}`,
                              background: "transparent",
                              padding: "10px 0",
                              fontSize: "clamp(2rem, 8vw, 2.6rem)",
                              lineHeight: 1.1,
                              color: theme.colors.text,
                            }}
                          />
                        </div>

                        <button
                          type="button"
                          disabled={!paypalSendHref}
                          onClick={() => {
                            if (paypalSendHref) {
                              window.location.assign(paypalSendHref);
                            }
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1px solid ${
                              paypalSendHref ? hexToRgba(theme.colors.primary, 0.62) : theme.colors.border
                            }`,
                            borderRadius: 999,
                            padding: "11px 20px",
                            background: "#ffffff",
                            color: paypalSendHref ? theme.colors.primary : theme.colors.textMuted,
                            fontWeight: 650,
                            fontSize: 18,
                            width: "100%",
                            cursor: paypalSendHref ? "pointer" : "not-allowed",
                            position: "relative",
                            textAlign: "center",
                          }}
                        >
                          <span style={{ paddingRight: 28 }}>
                            {paypal.isPool
                              ? copy.paypalPoolAction(paypalAmountLabel)
                              : copy.paypalAction(paypalAmountLabel)}
                          </span>
                          <span
                            aria-hidden="true"
                            style={{
                              position: "absolute",
                              right: 18,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontWeight: 800,
                              fontSize: 19,
                              color: paypalSendHref ? theme.colors.primary : theme.colors.textMuted,
                              lineHeight: 1,
                            }}
                          >
                            P
                          </span>
                        </button>

                        <p
                          style={{
                            margin: 0,
                            fontSize: 12,
                            color: theme.colors.textMuted,
                            textAlign: "center",
                          }}
                        >
                          {paypal.isPool ? copy.paypalPoolSecurity : copy.paypalSecurity}
                        </p>
                      </Stack>
                    </div>
                  ) : null}

                  {additionalPaymentDetails.length > 0 ? (
                    <div
                      style={{
                        borderRadius: 28,
                        border: `1px solid ${paymentCardTone.border}`,
                        background: paymentCardTone.background,
                        padding: "24px 24px 26px",
                        height: "100%",
                      }}
                    >
                      <Stack gap="md">
                        <div style={{ display: "grid", gap: 6 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 16,
                            }}
                          >
                            <h3
                              style={{
                                margin: 0,
                                fontFamily: theme.fonts.heading,
                                color: paymentCardTone.accent,
                                fontSize: "clamp(1.8rem, 6vw, 2.4rem)",
                                lineHeight: 1.15,
                              }}
                            >
                              {paymentCardTitle}
                            </h3>
                            <span
                              style={{
                                borderRadius: 999,
                                padding: "8px 12px",
                                background: hexToRgba(paymentCardTone.accent, 0.12),
                                color: paymentCardTone.accent,
                                fontSize: 12,
                                fontWeight: 800,
                                letterSpacing: 1.2,
                              }}
                            >
                              {paymentCardTone.badge}
                            </span>
                          </div>
                          <p style={{ margin: 0, color: theme.colors.textMuted, lineHeight: 1.6 }}>
                            {paymentCardHint}
                          </p>
                        </div>

                        <div style={{ display: "grid", gap: 14 }}>
                          {additionalPaymentDetails.map((detail, index) => {
                            const href = toDetailHref(detail.value);
                            const isExternal = href?.startsWith("http");
                            const isPhone = href?.startsWith("tel:");
                            const canCopyDetail = hasCopyablePaymentValue(detail.value);
                            const detailCopyKey = `payment-${index}-${detail.label}`;

                            return (
                              <div
                                key={`${detail.label}-${detail.value}`}
                                style={{
                                  display: "grid",
                                  gap: 4,
                                  paddingBottom: 14,
                                  borderBottom: `1px solid ${theme.colors.border}`,
                                }}
                              >
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    letterSpacing: 1.2,
                                    textTransform: "uppercase",
                                    color: theme.colors.textMuted,
                                  }}
                                >
                                  {detail.label}
                                </p>
                                <p
                                  style={{
                                    margin: 0,
                                    color: theme.colors.text,
                                    fontSize: 20,
                                    lineHeight: 1.35,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {detail.value}
                                </p>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                                  {href ? (
                                    <a
                                      href={href}
                                      target={isExternal ? "_blank" : undefined}
                                      rel={isExternal ? "noreferrer" : undefined}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 999,
                                        padding: "9px 14px",
                                        border: `1px solid ${hexToRgba(paymentCardTone.accent, 0.28)}`,
                                        background: hexToRgba(paymentCardTone.accent, 0.1),
                                        color: paymentCardTone.accent,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        textDecoration: "none",
                                      }}
                                    >
                                      {isPhone ? copy.callNumberAction : copy.openLinkAction}
                                    </a>
                                  ) : null}
                                  {canCopyDetail ? (
                                    <button
                                      type="button"
                                      onClick={() => void copyValue(detail.value, detailCopyKey)}
                                      style={{
                                        borderRadius: 999,
                                        border: `1px solid ${theme.colors.border}`,
                                        padding: "9px 14px",
                                        background:
                                          copiedDetailKey === detailCopyKey
                                            ? hexToRgba(paymentCardTone.accent, 0.12)
                                            : "#ffffff",
                                        color:
                                          copiedDetailKey === detailCopyKey
                                            ? paymentCardTone.accent
                                            : theme.colors.text,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                      }}
                                    >
                                      {copiedDetailKey === detailCopyKey ? copy.copiedButton : copy.copyButton}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Stack>
                    </div>
                  ) : null}
                </div>
              </Stack>
            </FadeInSection>
          ) : null}
        </Stack>
      </Container>

      <AnimatePresence>
        {activeGift ? (
          <GiftSelectionModal
            gift={activeGift}
            status={computeStatus(activeGift)}
            currency={safeCurrency(activeGift)}
            onClose={closeGiftModal}
            onAddParticipant={async (participant) => {
              try {
                return await handleAddParticipant(activeGift.id, participant);
              } catch (error) {
                if (error instanceof ApiClientError) {
                  throw new Error(error.message);
                }
                throw error;
              }
            }}
          />
        ) : null}
      </AnimatePresence>
    </SectionBackdrop>
  );
}
